import React, { useState } from "react";
import { ContactSupport } from "@mui/icons-material";
import { Box, Button, Checkbox, Divider, FormControlLabel, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { apiSlice, useGetAccountModulesQuery, useGetProjectQuery } from "fond/api";
import { inputLayerGroups, LayerGroupIds, LayerIds } from "fond/layers";
import { getCurrentProjectData, getCurrentProjectUploads } from "fond/project";
import { beginLayerUpload, dismissUploadError } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";
import { isExtendedPlannerProject } from "fond/utils/project";

import AddressTypeAlerts from "./AddressTypeAlerts";
import Upload from "./Upload";
import UploadProgress from "./UploadProgress";
import { useAddressTypeCheck } from "./useAddressTypeCheck";

import { Alert, AlertTitle, DefaultAlert } from "../InputDataPanel.styles";

interface LayerGroupUploadFormProps {
  layerGroupId: string;
  /** Skip the confirmation shown before replacing existing data. */
  defaultIsReplacing?: boolean;
  /** Whether to show the layer group name above the form. */
  showLayerName?: boolean;
  onCancel(): void;
  onSuccess(): void;
}

/**
 * Upload form, replace confirmation and upload progress for the layers of a layer group.
 */
const LayerGroupUploadForm: React.FC<LayerGroupUploadFormProps> = ({
  layerGroupId,
  defaultIsReplacing = false,
  showLayerName = true,
  onCancel,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const inputLayerGroup = inputLayerGroups[layerGroupId];
  const { projectId, versionId } = useAppSelector((state) => state.project);
  const dataLayers = useAppSelector((state) => getCurrentProjectData(state.project)?.layers) || [];
  const upload = useAppSelector((state) => inputLayerGroup && getCurrentProjectUploads(state.project)[inputLayerGroup.id]);
  const { data: project } = useGetProjectQuery(projectId, { skip: !projectId });

  const [isReplacing, setIsReplacing] = useState(defaultIsReplacing);
  const [convertToDualSided, setConvertToDualSided] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<{ [layerId: string]: File[] | null }>({});
  const addressTypeCheck = useAddressTypeCheck();
  const { selectedAddressTypeField } = addressTypeCheck;

  const dataExists =
    inputLayerGroup &&
    inputLayerGroup.layers.some((layer) => {
      return (dataLayers[layer.id]?.features?.length ?? 0) > 0;
    });
  const hasSelectedFiles = Object.values(selectedFiles).some((v) => v != null);

  const autoUpload = [LayerGroupIds.inExchange].includes(layerGroupId);

  const { data: accountModules } = useGetAccountModulesQuery(project?.Account?.ID ?? skipToken);
  const canSelectDualSided = !isExtendedPlannerProject(project) && accountModuleCheck(accountModules, Actions.SELECT_UNDERGROUND_PATH_DUAL_SIDED);

  const onDismissUploadError = (layerId: string) => dispatch(dismissUploadError(layerId));
  const onSuccessfulUpload = () => {
    onSuccess();
    dispatch(apiSlice.util.invalidateTags([{ type: "Version", id: versionId }]));
  };

  const onBeginUpload = (uploads: { [layerId: string]: File[] | null }, addressType: string) => {
    dispatch(beginLayerUpload(projectId, versionId, inputLayerGroup.id, uploads, convertToDualSided, addressType, onSuccessfulUpload));
  };

  const handleSingleUploadDrop = (files: File[]) => {
    const { layers } = inputLayerGroup;
    if (layers.length !== 1) {
      throw new Error("Should have exactly one layer if we get here");
    }
    onBeginUpload({ [layers[0].id]: files }, selectedAddressTypeField);
  };

  if (!upload && dataExists && !isReplacing) {
    return (
      <Box px={1}>
        {showLayerName && (
          <Typography fontWeight="500" mb={2}>
            {inputLayerGroup.name}
          </Typography>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Replace data</AlertTitle>
          <Typography variant="body3" component="p" mb={1}>
            You have already uploaded {inputLayerGroup.name} for this project.
          </Typography>
          <Typography variant="body3" component="p">
            If you like, you can replace them.
          </Typography>
        </Alert>
        <Divider />
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
          <Button color="primary" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={() => setIsReplacing(true)} sx={{ ml: 1, px: 2 }} data-testid="replace-button">
            Replace
          </Button>
        </Box>
      </Box>
    );
  }

  if (upload) {
    return (
      <>
        {showLayerName && (
          <Box px={1} mb={2}>
            <Typography fontWeight="500">{inputLayerGroup.name}</Typography>
          </Box>
        )}
        <UploadProgress upload={upload} onDismissUploadError={() => onDismissUploadError(inputLayerGroup.id)} inputLayer={inputLayerGroup.id} />
      </>
    );
  }

  return (
    <Box px={1}>
      <AddressTypeAlerts {...addressTypeCheck} />
      {layerGroupId === LayerGroupIds.spanPole && (
        <Box px={1}>
          <Typography variant="caption" sx={{ color: (theme) => theme.palette.biarri.secondary.grey }}>
            You may upload Aerial spans, Poles, or both
          </Typography>
        </Box>
      )}
      {inputLayerGroup &&
        inputLayerGroup.layers.map((layer) => (
          <Upload
            key={layer.id}
            layer={layer}
            autoUpload={autoUpload}
            onBeginUpload={handleSingleUploadDrop}
            onSelectFiles={(files: File[]) => {
              setSelectedFiles((prev) => ({ ...prev, [layer.id]: files }));
            }}
            onRemove={() => {
              setSelectedFiles((prev) => ({ ...prev, [layer.id]: null }));
              if (layer.id === LayerIds.inAddress) {
                addressTypeCheck.reset();
              }
            }}
            layerValidator={(features) => {
              if (layer.id === LayerIds.inAddress) {
                addressTypeCheck.checkAddresses(features);
              }
            }}
            convertToDualSidedField={
              canSelectDualSided && layer.id === LayerIds.inStreet ? (
                <Box my={1} p={1}>
                  <Typography fontWeight="500">Advanced control</Typography>
                  <FormControlLabel
                    control={<Checkbox checked={convertToDualSided} onChange={() => setConvertToDualSided((prev) => !prev)} />}
                    label={<Typography variant="body3">Convert to dual-sided</Typography>}
                  />
                  <Typography variant="caption" color={(theme) => theme.palette.biarri.secondary.grey} component="p" letterSpacing={0.53}>
                    FOND will approximate underground with streets and can convert streets centerlines to a dual-sided network.
                  </Typography>
                </Box>
              ) : null
            }
          />
        ))}
      {layerGroupId === LayerGroupIds.inAddress && (
        <DefaultAlert
          sx={{ mb: 1.5 }}
          severity="info"
          icon={<ContactSupport sx={{ fontSize: "inherit", color: (theme) => theme.palette.biarri.secondary.activeGrey }} />}
        >
          <Box>
            <Typography variant="h7" component="p" letterSpacing={0.15}>
              You can control how each address point is served after the upload is complete.
            </Typography>
            <Button
              variant="text"
              sx={{ padding: 0, fontSize: (theme) => theme.typography.size.small, mt: 1 }}
              component="a"
              href="https://fondhelp.biarrinetworks.com/how-to-control-fiber-allocation-in-fond"
              target="_blank"
              rel="noreferrer"
            >
              Learn it here
            </Button>
          </Box>
        </DefaultAlert>
      )}
      {!autoUpload && (
        <Box>
          <Divider />
          <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
            <Button color="primary" size="small" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!hasSelectedFiles}
              onClick={() => onBeginUpload(selectedFiles, selectedAddressTypeField)}
              sx={{ ml: 1, px: 2 }}
              data-testid="upload-button"
            >
              Upload
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LayerGroupUploadForm;
